import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { fetchUserClaims, addNewClaim, updateUserClaim, clearErrors } from '../../store/slices/claimManagementSlice';
import { sendClaimSubmissionNotification } from '../../emailjs/emailService';
import ClaimDetailModal from './components/ClaimDetailModal';
import './ClaimManagement.scss';

const ClaimManagement = () => {
  const dispatch = useDispatch();
  const { claims, loading, error, addingClaim, addClaimError, updatingClaim } = useSelector(state => state.claimManagement);
  const { user } = useSelector(state => state.auth);
  
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [fileUpload, setFileUpload] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  
  useEffect(() => {
    if (user?.email) {
      dispatch(fetchUserClaims(user.email));
    }
    
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch, user]);
  
  const onFileChange = (e) => {
    if (e.target.files[0]) {
      setFileUpload(e.target.files[0]);
    }
  };
  
  const onSubmitClaim = async (data) => {
    if (!fileUpload) {
      alert('Please upload a supporting document');
      return;
    }
    
    const claimData = {
      ...data,
      empID: user.empID || 'N/A',
      email: user.email,
      name: user.displayName || user.email.split('@')[0],
      status: 'Pending',
      amount: parseFloat(data.amount),
      expenseDate: new Date(data.expenseDate)
    };
    
    try {
      setFileUploading(true);
      await dispatch(addNewClaim({ claimData, file: fileUpload })).unwrap();
      await sendClaimSubmissionNotification(claimData, {
        email: user.email,
        name: user.displayName || user.name || user.email.split('@')[0],
        displayName: user.displayName || user.name || user.email.split('@')[0],
        firstName: (user.displayName || user.name || '').split(' ')[0] || 'Employee',
        lastName: '',
      });
      reset();
      setFileUpload(null);
      alert('Claim submitted successfully');
    } catch (err) {
      console.error('Failed to submit claim:', err);
    } finally {
      setFileUploading(false);
    }
  };
  
  const viewClaimDetails = (claim) => {
    setSelectedClaim(claim);
    setShowDetailModal(true);
  };

  const handleUpdateClaim = async ({ claimId, claimData, file }) => {
    await dispatch(updateUserClaim({ claimId, claimData, file })).unwrap();
  };
  
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedClaim(null);
  };

  const activeSelectedClaim = selectedClaim
    ? claims.find((item) => item.id === selectedClaim.id) || selectedClaim
    : null;
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      Pending: { color: 'warning', text: 'Pending' },
      Approved: { color: 'success', text: 'Approved' },
      Rejected: { color: 'danger', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };
  
  return (
    <div className="claim-management-container">
      <div className="page-header">
        <h2>Claim Management</h2>
        <p>Submit and track your expense claims</p>
      </div>
      
      <div className="content-section">
        <div className="submit-claim-section">
          <div className="section-header">
            <h3>Submit New Claim</h3>
          </div>
          
          <form onSubmit={handleSubmit(onSubmitClaim)} className="claim-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invoiceNo">
                  Invoice No. <span className="required">*</span>
                </label>
                <input
                  id="invoiceNo"
                  type="text"
                  {...register('invoiceNo', { required: 'Invoice number is required' })}
                  className={errors.invoiceNo ? 'error' : ''}
                />
                {errors.invoiceNo && <span className="error-message">{errors.invoiceNo.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="expenseDate">
                  Expense Date <span className="required">*</span>
                </label>
                <input
                  id="expenseDate"
                  type="datetime-local"
                  {...register('expenseDate', { required: 'Expense date is required' })}
                  className={errors.expenseDate ? 'error' : ''}
                />
                {errors.expenseDate && <span className="error-message">{errors.expenseDate.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="title">
                  Claim Title <span className="required">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  className={errors.title ? 'error' : ''}
                />
                {errors.title && <span className="error-message">{errors.title.message}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expensePurpose">
                  Expense Purpose <span className="required">*</span>
                </label>
                <select
                  id="expensePurpose"
                  {...register('expensePurpose', { required: 'Purpose is required' })}
                  className={errors.expensePurpose ? 'error' : ''}
                >
                  <option value="">Select Expense Purpose</option>
                  <option value="Office Expense">Office Expense</option>
                  <option value="Business Trip">Business Trip</option>
                  <option value="Personal">Personal Expense</option>
                  <option value="Other">Other</option>
                </select>
                {errors.expensePurpose && <span className="error-message">{errors.expensePurpose.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="currency">
                  Currency <span className="required">*</span>
                </label>
                <select
                  id="currency"
                  {...register('currency', { required: 'Currency is required' })}
                  className={errors.currency ? 'error' : ''}
                  defaultValue="€"
                >
                  <option value="€">€ (Euro)</option>
                  <option value="$">$ (USD)</option>
                  <option value="£">£ (GBP)</option>
                </select>
                {errors.currency && <span className="error-message">{errors.currency.message}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="amount">
                  Amount <span className="required">*</span>
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  {...register('amount', { 
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' }
                  })}
                  className={errors.amount ? 'error' : ''}
                />
                {errors.amount && <span className="error-message">{errors.amount.message}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group file-upload">
                <label htmlFor="supportingDoc">
                  Supporting Document <span className="required">*</span>
                </label>
                <input
                  id="supportingDoc"
                  type="file"
                  onChange={onFileChange}
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                {!fileUpload && <span className="help-text">Please upload an invoice or receipt</span>}
                {fileUpload && <span className="file-name">{fileUpload.name}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="remarks">
                  Remarks <span className="required">*</span>
                </label>
                <textarea
                  id="remarks"
                  {...register('remarks', { required: 'Remarks are required' })}
                  className={errors.remarks ? 'error' : ''}
                  rows="3"
                ></textarea>
                {errors.remarks && <span className="error-message">{errors.remarks.message}</span>}
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={addingClaim || fileUploading || !fileUpload}
              >
                {(addingClaim || fileUploading) ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : 'Submit Claim'}
              </button>
            </div>
            
            {addClaimError && (
              <div className="error-alert">
                Failed to submit claim: {addClaimError}
              </div>
            )}
          </form>
        </div>
        
        <div className="my-claims-section">
          <div className="section-header">
            <h3>My Claims</h3>
          </div>
          
          {loading && claims.length === 0 ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading your claims...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>{error}</p>
              <button onClick={() => dispatch(fetchUserClaims(user.email))}>Try Again</button>
            </div>
          ) : claims.length === 0 ? (
            <div className="no-claims-container">
              <p>You don't have any claims yet.</p>
            </div>
          ) : (
            <div className="claims-table-container">
              <table className="claims-table">
                <thead>
                  <tr>
                    <th>Invoice No.</th>
                    <th>Title</th>
                    <th>Expense Date</th>
                    <th>Claim Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map(claim => (
                    <tr key={claim.id} className={claim.status === 'Rejected' ? 'rejected' : ''}>
                      <td>{claim.invoiceNo}</td>
                      <td className="claim-title">{claim.title}</td>
                      <td>{formatDate(claim.expenseDate)}</td>
                      <td>{formatDate(claim.claimDate)}</td>
                      <td className="claim-amount">
                        {claim.currency} {claim.amount.toFixed(2)}
                      </td>
                      <td>{getStatusBadge(claim.status)}</td>
                      <td>
                        <button 
                          className="view-button"
                          onClick={() => viewClaimDetails(claim)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {showDetailModal && activeSelectedClaim && (
        <ClaimDetailModal 
          claim={activeSelectedClaim}
          onClose={closeDetailModal}
          onUpdate={handleUpdateClaim}
          isUpdating={updatingClaim}
        />
      )}
    </div>
  );
};

export default ClaimManagement; 